import { useMemo, useState } from 'react'

import { ChevronDown } from 'lucide-react'

import { useSwitchTenant } from '@/features/tenant/hooks/use-switch-tenant'
import { useIsDesktop } from '@/hooks/use-desktop'

import { useAdminTenants } from '@/shared/auth/hooks/use-admin-tenants'
import { useSsoAllowedTenants } from '@/shared/auth/hooks/use-sso-allowed-tenants'
import { useUserTenants } from '@/shared/auth/hooks/use-user-tenants'
import { useCurrentTenant } from '@/shared/auth/store'

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerTitle,
	DrawerTrigger,
} from '@/components/ui/drawer'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'

import type { Tenant } from '@/shared/auth/types'

type TenantSettingsSelectProps = {
	adminOnly?: boolean
	/** Restrict the list to tenants whose SKU permits SSO (`sku_info.sso_allowed`). */
	ssoOnly?: boolean
}

export const TenantSettingsSelect = ({
	adminOnly = false,
	ssoOnly = false,
}: TenantSettingsSelectProps) => {
	const isDesktop = useIsDesktop()
	const currentTenantID = useCurrentTenant()
	const userTenants = useUserTenants()
	const adminTenants = useAdminTenants()
	const switchTenant = useSwitchTenant()

	const baseTenants = adminOnly ? adminTenants : userTenants
	const ssoAllowedTenants = useSsoAllowedTenants(ssoOnly ? baseTenants : [])
	const allowedTenants = ssoOnly ? ssoAllowedTenants : baseTenants

	const [open, setOpen] = useState(false)

	// Derive from baseTenants, not the SSO-filtered list: the route loader already
	// guarantees the current tenant is SSO-allowed, and ssoAllowedTenants is empty
	// while its queries resolve — using it here would flash "Select Tenant".
	const selectedTenant = useMemo(() => {
		if (!currentTenantID) return null
		return baseTenants.find((tenant) => tenant.id === currentTenantID) ?? null
	}, [currentTenantID, baseTenants])

	const handleSelect = async (tenantId: string | undefined) => {
		setOpen(false)
		await switchTenant(tenantId)
	}

	if (isDesktop) {
		return (
			<>
				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger className="cursor-pointer">
						<p className="flex flex-row items-center text-4xl font-bold">
							{selectedTenant ? (
								<>{selectedTenant.fullname}</>
							) : (
								<>Select Tenant</>
							)}
							<ChevronDown className="ml-2 h-4 w-4" />
						</p>
					</PopoverTrigger>
					<PopoverContent className="w-64 p-0" align="start">
						<TenantList
							onSelectTenant={handleSelect}
							tenants={allowedTenants}
						/>
					</PopoverContent>
				</Popover>
			</>
		)
	}

	return (
		<>
			<Drawer open={open} onOpenChange={setOpen}>
				<DrawerTrigger asChild>
					<p className="flex flex-row items-center text-4xl font-bold">
						{selectedTenant ? (
							<>{selectedTenant.fullname}</>
						) : (
							<>Select Tenant</>
						)}
						<ChevronDown className="ml-2 h-4 w-4" />
					</p>
				</DrawerTrigger>
				<DrawerContent id="select-tenant-drawer" className="p-4">
					<DrawerTitle>Tenants</DrawerTitle>
					<DrawerDescription>Select Tenant</DrawerDescription>
					<div className="mt-4 border-t">
						<TenantList
							onSelectTenant={handleSelect}
							tenants={allowedTenants}
						/>
					</div>
				</DrawerContent>
			</Drawer>
		</>
	)
}

type TenantListProps = {
	onSelectTenant: (tenantId: string | undefined) => void
	tenants: Array<Tenant>
}

const TenantList = ({ onSelectTenant, tenants }: TenantListProps) => {
	return (
		<Command>
			<CommandInput placeholder="Select Tenant" />
			<CommandList>
				<CommandEmpty>No results found.</CommandEmpty>
				<CommandGroup>
					{tenants.map((tenant) => (
						<CommandItem
							key={tenant.id}
							value={tenant.fullname}
							// Close over tenant.id directly — don't round-trip through the
							// cmdk value string, which is trimmed and brittle to match back.
							onSelect={() => onSelectTenant(tenant.id)}
						>
							{tenant.fullname}
						</CommandItem>
					))}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}
